import { ContentModel, SearchSkuInformationModel } from '@bizzkit/ecommerce-search/client';
import {
	ImageResponse,
	IncludedItemMetaData,
	ProductCategoryId,
	ProductMasterId,
	ResourceLocation,
	SkuMediaMetaData,
	VariantAttributeValueMetadata,
	VariantSelectorAttributeMetadata,
} from '@/generated/data.schemas';
import { PageMetadata } from '@/routes/_head.partial';
import { MediaSet } from '@/types/media';

export interface ProductItemResponse {
	id: ProductMasterId;
	itemNo?: string | null;
	name: string;
	brand: string;
	url?: string | null;
	image: ImageResponse;

	/** from DIH endpoint */
	price?: number;

	stock?: string;
	variants?: ProductSimpleVariant[];
	skus?: SearchSkuInformationModel[] | null;
	quantity?: number;
}

export interface AddOnProduct {
	readonly image: MediaSet | ImageResponse | null;
	readonly name: string;
	readonly id: string;
	readonly itemNo?: string | null;
	readonly brand?: string;
	readonly quantity?: number;
}

export interface Filter {
	id: string;
	type: FilterType;
	displayName?: string | null;
	min?: number | null;
	max?: number | null;
	selectedMin?: number | null;
	selectedMax?: number | null;
	values?: FilterValue[] | null;
}

export enum FilterType {
	String = 'String',
	Number = 'Number',
}

export interface FilterValue {
	label: string;
	value: string;
	hitCount?: number | null;
	isSelected: boolean;
}

export interface PagingInformation {
	currentPage: number;
	pageSize: number;
	totalNumberOfItems: number;
}

export interface PriceSliderValues {
	minPrice?: number | null;
	maxPrice?: number | null;
	selectedMinPrice?: number | null;
	selectedMaxPrice?: number | null;
}

export interface ProductDetail {
	id: string;
	itemNo?: string | null;
	name: string;
	brand?: string;
	stock: number;
	price?: number;
	variants: ProductVariant[];
	siblings: ProductSibling[];
	shortDescription: string;
	longDescription: string;
	images: ImageResponse[];
	specs: Record<string, string>;
	videos: ProductVideo[];
	files: ProductFile[];
	inserts: IncludedItemMetaData[];
	breadcrumbs: string[];
	pageMetadata: PageMetadata;
	variantSelectors: VariantSelectorAttributeMetadata[];
	initialVariant?: ProductVariant;
	rawInserts?: string[];
	splashText?: string[];
}

export interface ProductFile {
	name: string;
	fileType: string;
	url: string;
}

export interface ProductSearchResult {
	phrase?: string | null;
	didYouMean?: string | null;
	products: ProductItemResponse[];
	filters: Filter[];
	currentSorting?: SortingOption;
	pagingInformation: PagingInformation;
	sorting: SortingOption[];
	priceSliderValues: PriceSliderValues;
	productCategoryIds: ProductCategoryId[];
	hasCategory: boolean;
}

export interface AppSolutionBuilderSearchResult {
	products: ProductItemResponse[];
	filters: Filter[];
}

export interface ProductSibling {
	id: string;
	name: string;
	url?: string | null;
	image?: ImageResponse;
}

export enum ProductSort {
	Name = 'Name',
	Score = 'Score',
	Price = 'Price',
}

export interface ProductSimpleVariant {
	id: string;
	name: string;
}

export interface ProductVariant {
	id: string;
	itemNo?: string | null;
	name: string;
	price?: number;
	stock?: number;
	size?: string;
	specs: Record<string, string>;
	selectors: VariantAttributeValueMetadata[];
	disableAddToBasket: boolean;
	inserts?: IncludedItemMetaData[] | null;
	mandatoryAddOns?: IncludedItemMetaData[] | null;
	spareParts?: IncludedItemMetaData[] | null;
	accessories?: string[] | null;
	associatedProducts?: AssociatedProductReference[] | null;
	shortDescription?: string | null;
	longDescription?: string | null;
	metaDescription?: string | null;
	metaKeywords?: string | null;
	metaTitle?: string | null;
	brand?: string | null;
	media?: SkuMediaMetaData;
	splashText?: string[] | null;
}

export interface AssociatedProductReference {
	categoryId: string;
	productMasterId: string;
}

export interface ProductVideo {
	title: string;
	url: string;
	uniqueVideoId: string;
	thumbnailUrl: string;
}

export interface QuickSearchResult {
	phrase: string;
	totalHits: number;
	totalContentHits: number;
	products: ProductItemResponse[];
	searchSuggestions: string[];
	content: ContentModel[];
}

export enum SortDirection {
	Ascending = 'Ascending',
	Descending = 'Descending',
}

export interface SortingOption {
	sortBy: ProductSort;
	sortDirection: SortDirection;
	displayName: string;
}

export enum MediaModelType {
	Image = 'Image',
	Video = 'Video',
	File = 'File',
}

export interface PrevLocationProps {
	pathname?: string;
	search?: string;
	state?: ResourceLocation;
}

// Reuse LocationDetails in both interfaces
export interface PrevLocation extends PrevLocationProps {}

export interface ResourceLocationWithPrev extends ResourceLocation {
	prevLocation?: PrevLocationProps;
}
